"use client";
import React, {
  SetStateAction,
  useState,
  useEffect,
  useRef,
  Dispatch,
  useCallback,
} from "react";
import {
  Modal,
  Box,
  Card,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useVoiceContext } from "@/context/voiceContext";
import { Mic, MicOff, RotateCcw, SendHorizontal, X } from "lucide-react";
import VoiceBackground from "./voice/voiceBackground";
import useDevice from "@/hooks/useDevice";
import gaService from "@/api/services/ga.service";

interface FullScreenSpeechInputProps {
  onSend: (text: string) => void;
  onClose?: () => void;
  setQuery?: Dispatch<SetStateAction<string>>;
}

interface AnimatedCardProps {
  listening?: boolean;
  isMobile?: boolean;
}

const AnimatedCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "listening" && prop !== "isMobile",
})<AnimatedCardProps>(({ listening, theme, isMobile }) => ({
  position: "relative",
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: isMobile ? "95%" : "65%",
  minHeight: 400,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundSize: "200% 200%",
  animation: listening ? "gradientWave 2s ease infinite" : "none",
  transform: listening ? "scale(1.02)" : "scale(1)",
  transition: "transform 0.2s ease",
  "@keyframes gradientWave": {
    "0%": { backgroundPosition: "0% 50%" },
    "50%": { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0% 50%" },
  },
  color: theme.palette.common.white,
  borderRadius: "80px",
}));

export default function FullScreenSpeechInput({
  onSend,
  onClose,
}: FullScreenSpeechInputProps) {
  const {
    isListening,
    error,
    transcript,
    startListening,
    stopListening,
  } = useVoiceContext();

  const editableRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const isMobile = useDevice()
  // When transcript updates and not editing, update DOM directly
  useEffect(() => {
    if (!isEditing && editableRef.current) {
      editableRef.current.textContent = transcript;
      // place caret at end if focused
      if (
        typeof window !== "undefined" &&
        window.document.activeElement === editableRef.current
      ) {
        const el = editableRef.current;
        const range = window.document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(el);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }, [transcript, isEditing]);
  useEffect(() => {
    const supportsSpeechRecognition = typeof window !== "undefined" && (
  (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
);

if(supportsSpeechRecognition){
  startListening();
}
else{
  alert("not supported")
}
  }, []);

  const handleFocus = () => setIsEditing(true);
  const handleBlur = () => setIsEditing(false);

  const resetTranscript = () => {
    if (typeof window !== "undefined" && editableRef.current) {
      gaService.voiceInputReset()
      editableRef.current.textContent = "";
      const el = editableRef.current;
      const range = window.document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
      setIsEditing(false);
    }
  };

  const sendTranscript = useCallback(() => {
    gaService.voiceInputSubmited()
    const text = editableRef.current?.textContent || "";
    onSend(text);
  }, [onSend]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger on plain Enter
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendTranscript();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [sendTranscript]);
  return (
    <Modal
      open
      onClose={onClose}
      closeAfterTransition
      BackdropProps={{
        sx: { bgcolor: "rgba(0,0,0,0.7)", backdropFilter: "blur(25px)" },
      }}
    >
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          p: 2,
        }}
      >
        <AnimatedCard elevation={12} listening={isListening} isMobile={isMobile}>
          <IconButton
            sx={{
              position: "absolute",
              zIndex: 9999999,
              top: 20,
              right: 30,
            }}
            onClick={onClose}
          >
            <X></X>
          </IconButton>
          {error && (
            <Typography
              variant="body2"
              color="error"
              align="center"
              sx={{ mt: 2 }}
            >
              {error}
            </Typography>
          )}

          <Typography
            variant="h1"
            fontWeight="medium"
            component="div"
            contentEditable
            suppressContentEditableWarning
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={editableRef}
            sx={{ textAlign: "center", width: "100%", outline: "none" }}
          />

          <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.2)" }} />
          <VoiceBackground />
        </AnimatedCard>

        <Box sx={{ mt: 4, display: "flex", alignContent: "center", gap: 2 }}>
          <IconButton
            sx={{
              p: 2,
              color: "rgba(255,255,255,0.4)",
              border: "2px solid rgba(255,255,255,0.4)",
            }}
            onClick={resetTranscript}
          >
            <RotateCcw />
          </IconButton>
          <IconButton
            onClick={isListening ? stopListening : startListening}
            sx={{
              p: 2,
              background: `linear-gradient(102.02deg, #3759FF 26.11%, #7720FF 111.17%)`,
            }}
          >
            {isListening ? <Mic /> : <MicOff />}
          </IconButton>
          <IconButton
            sx={{
              p: 2,
              color: "rgba(255,255,255,0.4)",
              border: "2px solid rgba(255,255,255,0.4)",
            }}
            onClick={sendTranscript}
          >
            <SendHorizontal />
          </IconButton>
        </Box>
      </Box>
    </Modal>
  );
}
